import { useEffect, useState } from "react";
import MSINav from "./MSINav";
import positions from "./data/msi-positions.json";
import { pnlPct, riskAction, actionColor } from "./msiRiskManager";
import { syndicationAlert } from "./msiSyndication";
import closedSeed from "./data/msi-closed.json";
import { closePosition } from "./msiTradeManager";

const panel = { background: "#171717", border: "1px solid #2A2A2A", borderRadius: 12, padding: 18 };
const card = { ...panel, padding: 16 };
const cellClip = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

function fmt(n, d = 2) {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return "-";
  return Number(n).toFixed(d);
}

function fmtMoney(n) {
  if (n === null || n === undefined || Number.isNaN(Number(n)) || Number(n) === 0) return "-";
  return `$${Number(n).toFixed(2)}`;
}

function fmtDateTime(value) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString([], { month: "numeric", day: "numeric", hour: "numeric", minute: "2-digit" });
}

function tradingDaysOpen(position, today = new Date()) {
  const startValue = position.opened_at || position.date_entered || position.opened;
  if (!startValue) return "-";

  const start = new Date(startValue);
  const endValue = position.closed_at || position.date_closed;
  const end = endValue ? new Date(endValue) : new Date(today);

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "-";

  let days = 0;
  const d = new Date(start);
  d.setHours(0, 0, 0, 0);

  const e = new Date(end);
  e.setHours(0, 0, 0, 0);

  while (d <= e) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) days += 1;
    d.setDate(d.getDate() + 1);
  }

  return Math.max(days - 1, 0);
}

function optionEntry(position) {
  return Number(position.option_entry || position.entry || 0);
}

function optionCurrent(position) {
  return Number(position.option_current || position.current || 0);
}

function stockEntry(position) {
  return Number(position.stock_entry || 0);
}

function stockCurrent(position) {
  return Number(position.stock_current || 0);
}

function actionReason(position) {
  const pnl = pnlPct(position);
  const dte = Number(position.dte ?? 999);
  const status = String(position.status || "").toUpperCase();
  const action = riskAction(position);

  if (action === "EXIT" && status === "TARGET_HIT" && dte <= 2) return "Target hit with low DTE. Thesis complete.";
  if (action === "TAKE_PROFIT" && status === "TARGET_HIT") return "Target hit. Lock gains.";
  if (action === "SCALE_OUT") return "Contract is up 100%+. Scale out.";
  if (action === "LOCK_PROFITS") return "Contract is up 50%+. Protect profits.";
  if (action === "TRAIL_STOP") return "Contract is up 25%+. Trail stop.";
  if (action === "CUT_LOSS") return "Loss exceeded -50%. Cut risk.";
  if (dte <= 1 && pnl > 0) return "Expiration risk. Take profit.";
  if (dte <= 1 && pnl <= 0) return "Expiration risk. Exit.";

  return "No urgent risk trigger.";
}



function DetailItem({ label, value }) {
  return (
    <div>
      <div style={{ color: "#A0A0A0", fontSize: 13, fontWeight: 800 }}>{label}</div>
      <div style={{ color: "white", fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}

export default function MSIPositions() {
  const [openKey, setOpenKey] = useState(null);
  const [openPositions, setOpenPositions] = useState([]);
const [closedPositions, setClosedPositions] = useState([]);

useEffect(() => {
  const savedOpen = localStorage.getItem("msi_open_positions");
  const savedClosed = localStorage.getItem("msi_closed_positions");

  setOpenPositions(savedOpen ? JSON.parse(savedOpen) : positions);
  setClosedPositions(savedClosed ? JSON.parse(savedClosed) : closedSeed);
}, []);

  const grid = "90px 125px 95px 70px 95px 170px 130px 115px 95px 115px";

  const sorted = [...openPositions].sort((a, b) => {
    const priority = { EXIT: 1, CUT_LOSS: 2, TAKE_PROFIT: 3, SCALE_OUT: 4, LOCK_PROFITS: 5, TRAIL_STOP: 6, HOLD: 7 };
    return (priority[riskAction(a)] || 99) - (priority[riskAction(b)] || 99);
  });

  const urgent = sorted.find((p) => ["EXIT", "CUT_LOSS", "TAKE_PROFIT"].includes(riskAction(p)));
  
function countByAction(action) {
  return openPositions.filter((p) => riskAction(p) === action).length;
}

  return (
    <div style={{ minHeight: "100vh", background: "#0B0B0B", color: "white", padding: 24 }}>
      <h1 style={{ fontSize: 40, marginBottom: 4 }}>MSI Positions</h1>
      <div style={{ color: "#A0A0A0", marginBottom: 16 }}>Live plays, risk actions, and syndication alerts.</div>

      <MSINav />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(5, minmax(140px, 1fr))", gap: 14, marginTop: 20, marginBottom: 20 }}>
        <div style={card}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Open Positions</div><div style={{ fontSize: 30, fontWeight: 900 }}>{openPositions.length}</div></div>
        <div style={card}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Exit Now</div><div style={{ fontSize: 30, fontWeight: 900, color: "#ef4444" }}>{countByAction("EXIT")}</div></div>
        <div style={card}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Take Profit</div><div style={{ fontSize: 30, fontWeight: 900, color: "#22c55e" }}>{countByAction("TAKE_PROFIT")}</div></div>
        <div style={card}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Protect Gains</div><div style={{ fontSize: 30, fontWeight: 900, color: "#f59e0b" }}>{countByAction("TRAIL_STOP") + countByAction("LOCK_PROFITS") + countByAction("SCALE_OUT")}</div></div>
        <div style={card}><div style={{ color: "#A0A0A0", fontWeight: 800 }}>Hold</div><div style={{ fontSize: 30, fontWeight: 900 }}>{countByAction("HOLD")}</div></div>
      </div>

      {urgent && (
        <div style={{ ...panel, marginBottom: 20, borderColor: actionColor(riskAction(urgent)), display: "flex", justifyContent: "space-between", gap: 18, alignItems: "center" }}>
          <div>
            <div style={{ color: "#A0A0A0", fontWeight: 800, marginBottom: 6 }}>Highest Priority</div>
            <div style={{ fontSize: 24, fontWeight: 900 }}>{urgent.ticker} {urgent.contract}</div>
            <div style={{ color: "#A0A0A0", marginTop: 4 }}>{actionReason(urgent)}</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ color: actionColor(riskAction(urgent)), fontSize: 28, fontWeight: 900 }}>{riskAction(urgent)}</div>
            <div style={{ color: pnlPct(urgent) >= 0 ? "#22c55e" : "#ef4444", fontWeight: 900 }}>
              {pnlPct(urgent) >= 0 ? "+" : ""}{fmt(pnlPct(urgent), 1)}%
            </div>
          </div>
        </div>
      )}

      <div style={{ ...panel, overflowX: "auto" }}>
        <div style={{ display: "grid", gridTemplateColumns: grid, minWidth: 1205, gap: 12, color: "#A0A0A0", fontWeight: 700, borderBottom: "1px solid #2A2A2A", paddingBottom: 10, marginBottom: 10 }}>
          <div>Ticker</div><div>Contract</div><div>P/L</div><div>DTE</div><div>Days Open</div><div>Target</div><div>Action</div><div>Alert</div><div>Details</div><div>Close</div>
        </div>

        {sorted.length === 0 && (
          <div style={{ padding: 24, color: "#A0A0A0" }}>
            No open positions. Closed trades are available under the Closed tab.
          </div>
        )}

        {sorted.map((p) => {
          const pnl = pnlPct(p);
          const action = riskAction(p);
          const alertText = syndicationAlert(p);
          const key = `${p.ticker}-${p.contract}-${p.opened_at || p.opened}`;
          const isOpen = openKey === key;

          return (
            <div key={key}>
              <div style={{ display: "grid", gridTemplateColumns: grid, minWidth: 1205, gap: 12, padding: "12px 0", borderBottom: isOpen ? "none" : "1px solid #242424", alignItems: "center" }}>
                <div style={{ fontWeight: 900 }}>{p.ticker}</div>
                <div style={{ fontWeight: 800 }}>{p.contract}</div>
                <div style={{ color: pnl >= 0 ? "#22c55e" : "#ef4444", fontWeight: 900 }}>{pnl >= 0 ? "+" : ""}{fmt(pnl, 1)}%</div>
                <div>{p.dte ?? "-"}</div>
                <div>{tradingDaysOpen(p)}</div>
                <div title={p.target} style={{ color: "#A0A0A0", ...cellClip }}>{p.target || "-"}</div>
                <div style={{ color: actionColor(action), fontWeight: 900 }}>{action}</div>
                <div>
                  {alertText ? (
                    <button onClick={() => navigator.clipboard.writeText(alertText)} style={{ background: "#2563eb", color: "white", border: "none", borderRadius: 8, padding: "8px 10px", fontWeight: 800, cursor: "pointer" }}>
                      Copy Alert
                    </button>
                  ) : "-"}
                </div>
                <div>
                  <button onClick={() => setOpenKey(isOpen ? null : key)} style={{ background: "#262626", color: "white", border: "1px solid #3A3A3A", borderRadius: 8, padding: "8px 10px", fontWeight: 800, cursor: "pointer" }}>
                    {isOpen ? "Hide" : "View"}
                  </button>
                </div>
                <div>
                  <button
                    onClick={() => {
                      const ok = window.confirm(`Close ${p.ticker} ${p.contract} and move it to Closed Trades?`);
                      if (!ok) return;
                      const result = closePosition(p, openPositions, closedPositions);
                      setOpenPositions(result.open);
                      setClosedPositions(result.closed);
                      setOpenKey(null);
                    }}
                    style={{
                      background: "#ef4444",
                      color: "white",
                      border: "none",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    Close
                  </button>
                </div>
              </div>

              {isOpen && (
                <div style={{ minWidth: 1205, background: "#101010", borderBottom: "1px solid #242424", padding: "16px 18px", display: "grid", gridTemplateColumns: "repeat(4, minmax(160px, 1fr))", gap: 18 }}>
                  <DetailItem label="Option Entry" value={fmtMoney(optionEntry(p))} />
                  <DetailItem label="Option Now" value={fmtMoney(optionCurrent(p))} />
                  <DetailItem label="Stock Entry" value={fmtMoney(stockEntry(p))} />
                  <DetailItem label="Stock Now" value={fmtMoney(stockCurrent(p))} />
                  <DetailItem label="Opened" value={fmtDateTime(p.opened_at || p.opened)} />
                  <DetailItem label="Expiration" value={p.expiration_date || "-"} />
                  <DetailItem label="Closed" value={fmtDateTime(p.closed_at || p.date_closed)} />
                  <DetailItem label="Result" value={p.result || "OPEN"} />
                  <DetailItem label="Thesis" value={p.thesis || "-"} />
                  <DetailItem label="Status" value={p.status || "-"} />
                  <DetailItem label="Reason" value={actionReason(p)} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
